import React, { useCallback, useMemo, useState } from 'react'

import { useDashboardPermissions } from '@components'
import { Suggestion } from '@components/common/Suggestion'
import { Button, Toasts, useMediaQueryContext } from '@fourthwall/components'
import { useWebViewBridge } from '@fourthwall/webview-bridge'

import { useSetShopFlagMutation } from '../../../../../Home/queries/useSetShopFlagMutation'
import { useCurrentShop } from '../../../../../hooks/useCurrentShop'
import { type Recommendation } from '../../../Recommendations.types'
import { useRecommendationsDismiss } from '../../../hooks/useRecommendationsDismiss'
import cartAbandonmentEmailPromoImage from '../assets/cart-abandonment-email-promo.png'
import { useNotificationShopPreferencesMutation } from '../queries/useNotificationShopPreferencesMutation'
import { useNotificationShopPreferencesQuery } from '../queries/useNotificationShopPreferencesQuery'

const FLAG_NAME = 'cart-abandoned-promo-added'

export const useCartAbandonmentEmailPromoRecommendation = (): {
  isLoadingCartAbandonmentEmailPromoRecommendation: boolean
  cartAbandonmentEmailPromoRecommendation: Recommendation
  includePromoCode: () => Promise<void>
} => {
  const media = useMediaQueryContext()
  const { methods } = useWebViewBridge()
  const [isLoading, setIsLoading] = useState(false)
  const { createOnDismiss } = useRecommendationsDismiss()
  const {
    currentShop,
    invalidateCurrentShopQuery,
    isLoading: isLoadingCurrentShop,
  } = useCurrentShop()
  const { setShopFlagMutation } = useSetShopFlagMutation()
  const { hasPermission } = useDashboardPermissions()
  const areNotificationsEnabled = hasPermission('settings.checkout.notifications')
  const { notificationShopPreferencesQuery } = useNotificationShopPreferencesQuery(undefined, {
    enabled: areNotificationsEnabled,
  })
  const { notificationShopPreferencesMutation } = useNotificationShopPreferencesMutation()

  const handleIncludePromoCode = useCallback(async () => {
    setIsLoading(true)
    try {
      await notificationShopPreferencesMutation.mutateAsync([
        {
          mailPreferences: {
            cartAbandoned: notificationShopPreferencesQuery.data?.mailPreferences.cartAbandoned!,
            cartAbandonedIncludePromoCode: true,
          },
        },
      ])

      await setShopFlagMutation.mutateAsync([
        {
          flag: FLAG_NAME,
        },
      ])

      await invalidateCurrentShopQuery()

      Toasts.notify('5% off promo added to abandoned checkout emails.', {
        type: 'success',
      })

      await methods.showInfoBanner?.('5% off promo added to abandoned checkout emails.')
    } catch (error) {
      Toasts.notify(
        'Could not add 5% off promo to abandoned checkout emails. Please try again later.',
      )
    } finally {
      setIsLoading(false)
    }
  }, [
    invalidateCurrentShopQuery,
    methods,
    notificationShopPreferencesMutation,
    notificationShopPreferencesQuery.data?.mailPreferences.cartAbandoned,
    setShopFlagMutation,
  ])

  const cartAbandonmentEmailPromoRecommendation = useMemo(() => {
    const handleDismiss = async () => {
      await setShopFlagMutation.mutateAsync([
        {
          flag: FLAG_NAME,
        },
      ])
      await invalidateCurrentShopQuery()
    }

    return {
      id: 'cartAbandonmentEmailPromo' as Recommendation['id'],
      enabled:
        currentShop?.metafields[FLAG_NAME] !== true &&
        notificationShopPreferencesQuery.data?.mailPreferences.cartAbandoned.type !== 'NONE' &&
        !notificationShopPreferencesQuery.data?.mailPreferences.cartAbandonedIncludePromoCode,
      isLoading: isLoadingCurrentShop,
      suggestion: (
        <Suggestion
          title="Boost sales with a discount in abandoned checkout emails"
          description={
            <p>
              Increase your checkout recovery rate up to 40% when you offer a a discount code in
              abandoned checkout reminder emails
            </p>
          }
          imageUrl={cartAbandonmentEmailPromoImage}
          content={
            <Button
              fullWidth={!media.tablet}
              disabled={
                isLoading || notificationShopPreferencesMutation.isLoading || isLoadingCurrentShop
              }
              label={
                media.tablet
                  ? 'Add 5% off promo to abandoned checkout emails'
                  : '5% off abandoned carts'
              }
              size="small"
              analytics="cartAbandonmentEmailPromo"
              onClick={handleIncludePromoCode}
            />
          }
          onDismiss={createOnDismiss(handleDismiss)}
        />
      ),
    } satisfies Recommendation
  }, [
    currentShop?.metafields,
    notificationShopPreferencesQuery.data?.mailPreferences,
    isLoadingCurrentShop,
    media.tablet,
    isLoading,
    notificationShopPreferencesMutation.isLoading,
    handleIncludePromoCode,
    createOnDismiss,
    setShopFlagMutation,
    invalidateCurrentShopQuery,
  ])

  return {
    isLoadingCartAbandonmentEmailPromoRecommendation:
      notificationShopPreferencesQuery.isInitialLoading && areNotificationsEnabled,
    cartAbandonmentEmailPromoRecommendation,
    includePromoCode: handleIncludePromoCode,
  }
}
